import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Eye, Minus, Plus } from "lucide-react";
import { formatINR } from "@/lib/format";
import { useCart } from "@/lib/cart";
import RatingStars from "@/components/common/RatingStars";
import { fadeUp } from "@/lib/motion";

export const ProductCard = ({ product, onQuickView }) => {
  const { addItem, items, updateQty, removeItem } = useCart();
  const discount = product.discount || 0;
  const variantLabel = product.variants?.[0]?.label || "Default";
  const cartKey = `${product.slug}__${variantLabel}`;
  const inCartQty = items.find((i) => i.key === cartKey)?.qty || 0;

  return (
    <motion.div
      variants={fadeUp}
      data-testid="product-card"
      className="group relative flex flex-col overflow-hidden rounded-[var(--acx-radius-md)] border border-[color:var(--acx-border)] bg-[var(--acx-card)] transition-[border-color,box-shadow] duration-300 hover:border-white/20 hover:shadow-[var(--acx-shadow-lift)]"
    >
      <Link to={`/product/${product.slug}`} className="relative block aspect-square overflow-hidden bg-black">
        <img
          src={product.images?.[0]}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        {discount > 0 && (
          <span className="absolute left-3 top-3 rounded-full bg-[#E11B22] px-2.5 py-1 text-[11px] font-semibold text-white">
            -{discount}%
          </span>
        )}
        <span className="absolute right-3 top-3 rounded-full border border-white/15 bg-black/50 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-white/80 backdrop-blur">
          {product.type}
        </span>
        <button
          type="button"
          data-testid="product-card-quick-view-button"
          onClick={(e) => {
            e.preventDefault();
            onQuickView && onQuickView(product);
          }}
          className="absolute bottom-3 right-3 inline-flex h-10 w-10 translate-y-2 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white opacity-0 backdrop-blur transition-all duration-300 hover:bg-black/80 group-hover:translate-y-0 group-hover:opacity-100"
          aria-label="Quick view"
        >
          <Eye size={17} />
        </button>
      </Link>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="mb-1 flex items-center gap-2">
          <RatingStars value={product.rating} size={13} />
          <span className="text-[12px] text-white/45">({product.reviewCount || 0})</span>
        </div>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-display text-[15px] font-semibold tracking-[-0.01em] text-white sm:text-[16px]">
            {product.name}
          </h3>
        </Link>
        <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-white/55">{product.shortDescription}</p>

        <div className="mt-auto flex items-end justify-between pt-4">
          <div className="flex flex-col">
            <span data-testid="product-card-price" className="text-[17px] font-semibold text-white">
              {formatINR(product.price)}
            </span>
            {product.mrp > product.price && (
              <span className="text-[12px] text-white/40 line-through">{formatINR(product.mrp)}</span>
            )}
          </div>
          {inCartQty > 0 ? (
            <div className="inline-flex items-center gap-1 rounded-[12px] bg-white p-1 text-black" data-testid="product-card-qty-stepper">
              <button
                type="button"
                aria-label="Decrease quantity"
                data-testid="product-card-qty-decrease"
                onClick={() => (inCartQty <= 1 ? removeItem(cartKey) : updateQty(cartKey, inCartQty - 1))}
                className="inline-flex h-8 w-8 items-center justify-center rounded-[9px] transition-colors hover:bg-black/10"
              >
                <Minus size={15} />
              </button>
              <span className="min-w-[22px] text-center text-[14px] font-semibold" data-testid="product-card-qty-value">{inCartQty}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                data-testid="product-card-qty-increase"
                onClick={() => updateQty(cartKey, inCartQty + 1)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-[9px] transition-colors hover:bg-black/10"
              >
                <Plus size={15} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="product-card-add-to-cart-button"
              onClick={() => addItem(product)}
              className="inline-flex items-center gap-2 rounded-[12px] bg-white px-3.5 py-2.5 text-[13px] font-semibold text-black transition-colors duration-200 hover:bg-white/90"
            >
              <ShoppingCart size={15} />
              Add
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
